import { useCallback, useMemo } from 'react'
import { Editor, Element as SlateElement } from 'slate'

import { Button } from 'core-components/library/button'

import {
  insertExpandBlock,
  insertSeparator,
  toggleBlock,
  toggleMark,
  toggleModifier,
} from '../../helpers/slateHelper'
import {
  ALIGIN_TYPES,
  BACKGROUND_COLORS,
  BLOCK_FORMATS,
  CustomEditor,
  FONT_COLORS,
  FONT_TYPES,
  INLINE_FORMATS,
} from '../../interface/custom-types'
import { TOOLBAR_BUTTONS } from '../../interface/IToolbar'
import { ActionBoxButton } from '../components/action/ActionBoxButton'
import { BlockStyleButton } from '../components/button/BlockStyleButton'
import { MarkStyleButton } from '../components/button/MarkStyleButton'
import { ColorsButton } from '../components/colors/ColorsButton'
import { ColumnOptionsButton } from '../components/columnOptions/ColumnOptionsButton'
import { BlockStyleDropDown } from '../components/dropDown/BlockStyleDropDown'
import { DropDownForToolbar } from '../components/dropDown/DropDownForToolbar'
import { FontSizeDropDown } from '../components/dropDown/fontSizeDropdown/FontSizeDropDown'
import { MenuForToolbar } from '../components/dropDown/MenuForToolbar'
import { FileButton } from '../components/file/FileButton'
import { IFrameButton } from '../components/IFrame/IFrameButton'
import { ImageButton } from '../components/image/ImageButton'
import { decreaseIndent, increaseIndent } from '../components/indent/indentButtons'
import { LinkButton } from '../components/link/LinkButton'
import { SeparatorButton } from '../components/Separator/SeparatorButton'
import { TableButton } from '../components/table/TableButton'
import { VideoButton } from '../components/video/VideoButton'
import { TOOLBAR_TITLES } from '../constants'
import {
  DISABLED_BLOCKS_BUTTONS,
  DISABLED_TEXT_BUTTONS,
  isButtonDisabled,
} from '../helpers/disableButtonHelper'

export const useToolbarButtons = (
  editor: CustomEditor,
  disabled: boolean,
  fontSizes?: number[]
) => {
  const elementWithDisabledButtons = useMemo(() => {
    const [element] = Editor.nodes(editor, {
      match: (node) =>
        SlateElement.isElement(node) &&
        [...DISABLED_BLOCKS_BUTTONS, ...DISABLED_TEXT_BUTTONS].includes(node.type as BLOCK_FORMATS),
    })

    return element?.[0]?.['type']
  }, [editor, editor.selection])

  const isDisabled = useCallback(
    (name: TOOLBAR_BUTTONS) => {
      return isButtonDisabled(name, elementWithDisabledButtons, disabled)
    },
    [elementWithDisabledButtons, disabled]
  )

  const toolbarButtons = useMemo(() => {
    return {
      [TOOLBAR_BUTTONS.Undo]: () => (
        <Button
          title={TOOLBAR_TITLES[TOOLBAR_BUTTONS.Undo]}
          iconButton={'kbase kbase-undo'}
          onClick={() => editor.undo()}
          variant={'text'}
          color={'grey'}
          disabled={disabled}
        />
      ),
      [TOOLBAR_BUTTONS.Redo]: () => (
        <Button
          title={TOOLBAR_TITLES[TOOLBAR_BUTTONS.Redo]}
          iconButton={'kbase kbase-redo'}
          onClick={() => editor.redo()}
          variant={'text'}
          color={'grey'}
          disabled={disabled}
        />
      ),
      [TOOLBAR_BUTTONS.Size]: () => {
        const disabled = isDisabled(TOOLBAR_BUTTONS.Size)

        return (
          <BlockStyleDropDown
            defaultValue={FONT_TYPES[0].style}
            formats={FONT_TYPES}
            label={'Size'}
            onToggle={toggleBlock}
            disabled={disabled}
            formatKeyField={'type'}
            component={DropDownForToolbar}
          />
        )
      },
      [TOOLBAR_BUTTONS.FontSize]: () => {
        const disabled = isDisabled(TOOLBAR_BUTTONS.FontSize)

        return <FontSizeDropDown fontSizes={fontSizes ?? []} disabled={disabled} />
      },
      [TOOLBAR_BUTTONS.Bold]: () => (
        <MarkStyleButton
          format={INLINE_FORMATS.bold}
          label={TOOLBAR_TITLES[TOOLBAR_BUTTONS.Bold]}
          icon={'kbase kbase-editor-bold'}
          onToggle={toggleMark}
          disabled={disabled}
        />
      ),
      [TOOLBAR_BUTTONS.Italic]: () => (
        <MarkStyleButton
          format={INLINE_FORMATS.italic}
          label={TOOLBAR_TITLES[TOOLBAR_BUTTONS.Italic]}
          icon={'kbase kbase-editor-italic'}
          onToggle={toggleMark}
          disabled={disabled}
        />
      ),
      [TOOLBAR_BUTTONS.Underline]: () => (
        <MarkStyleButton
          format={INLINE_FORMATS.underline}
          label={TOOLBAR_TITLES[TOOLBAR_BUTTONS.Underline]}
          icon={'kbase kbase-editor-underline'}
          onToggle={toggleMark}
          disabled={disabled}
        />
      ),
      [TOOLBAR_BUTTONS.Strikethrough]: () => (
        <MarkStyleButton
          format={INLINE_FORMATS.strikethrough}
          label={TOOLBAR_TITLES[TOOLBAR_BUTTONS.Strikethrough]}
          icon={'kbase kbase-editor-strikethrough'}
          onToggle={toggleMark}
          disabled={disabled}
        />
      ),
      [TOOLBAR_BUTTONS.Superscript]: () => (
        <MarkStyleButton
          format={INLINE_FORMATS.superscript}
          label={TOOLBAR_TITLES[TOOLBAR_BUTTONS.Superscript]}
          icon={'kbase kbase-editor-sup'}
          onToggle={toggleMark}
          disabled={disabled}
        />
      ),
      [TOOLBAR_BUTTONS.Subscript]: () => (
        <MarkStyleButton
          format={INLINE_FORMATS.subscript}
          label={TOOLBAR_TITLES[TOOLBAR_BUTTONS.Subscript]}
          icon={'kbase kbase-editor-sub'}
          onToggle={toggleMark}
          disabled={disabled}
        />
      ),
      [TOOLBAR_BUTTONS.Color]: () => (
        <ColorsButton isBackgroundColor={false} colors={FONT_COLORS} disabled={disabled} />
      ),
      [TOOLBAR_BUTTONS.BackgroundColor]: () => (
        <ColorsButton isBackgroundColor={true} colors={BACKGROUND_COLORS} disabled={disabled} />
      ),
      [TOOLBAR_BUTTONS.Align]: () => (
        <BlockStyleDropDown
          defaultValue={ALIGIN_TYPES[0].style}
          formats={ALIGIN_TYPES}
          label={TOOLBAR_TITLES[TOOLBAR_BUTTONS.Align]}
          onToggle={toggleModifier}
          disabled={disabled}
          formatKeyField={'align'}
          component={MenuForToolbar}
        />
      ),
      [TOOLBAR_BUTTONS.BulletedList]: () => {
        const disabled = isDisabled(TOOLBAR_BUTTONS.BulletedList)

        return (
          <BlockStyleButton
            format={BLOCK_FORMATS.bulletedList}
            label={TOOLBAR_TITLES[TOOLBAR_BUTTONS.BulletedList]}
            icon={'kbase kbase-editor-ul'}
            onToggle={toggleBlock}
            disabled={disabled}
          />
        )
      },
      [TOOLBAR_BUTTONS.NumberedList]: () => {
        const disabled = isDisabled(TOOLBAR_BUTTONS.NumberedList)

        return (
          <BlockStyleButton
            format={BLOCK_FORMATS.numberedList}
            label={TOOLBAR_TITLES[TOOLBAR_BUTTONS.NumberedList]}
            icon={'kbase kbase-editor-ol'}
            onToggle={toggleBlock}
            disabled={disabled}
          />
        )
      },
      [TOOLBAR_BUTTONS.BlockQuote]: () => {
        const disabled = isDisabled(TOOLBAR_BUTTONS.BlockQuote)

        return (
          <BlockStyleButton
            format={BLOCK_FORMATS.blockQuote}
            label={TOOLBAR_TITLES[TOOLBAR_BUTTONS.BlockQuote]}
            icon={'kbase kbase-editor-blockquote'}
            onToggle={toggleBlock}
            disabled={disabled}
          />
        )
      },
      [TOOLBAR_BUTTONS.CodeBlock]: () => {
        const disabled = isDisabled(TOOLBAR_BUTTONS.CodeBlock)

        return (
          <BlockStyleButton
            format={BLOCK_FORMATS.code}
            label={TOOLBAR_TITLES[TOOLBAR_BUTTONS.CodeBlock]}
            icon={'kbase kbase-code'}
            onToggle={toggleBlock}
            disabled={disabled}
          />
        )
      },
      [TOOLBAR_BUTTONS.ExpandBlock]: () => {
        const disabled = isDisabled(TOOLBAR_BUTTONS.ExpandBlock)

        return (
          <Button
            title={TOOLBAR_TITLES[TOOLBAR_BUTTONS.ExpandBlock]}
            iconButton="kbase kbase-editor-expand-block"
            variant={'text'}
            color={'grey'}
            onClick={() => insertExpandBlock(editor)}
            disabled={disabled}
          />
        )
      },
      [TOOLBAR_BUTTONS.Increase]: () => (
        <Button
          title={TOOLBAR_TITLES[TOOLBAR_BUTTONS.Increase]}
          iconButton="kbase kbase-editor-indent-increase"
          variant={'text'}
          color={'grey'}
          onClick={() => increaseIndent(editor)}
          disabled={disabled}
        />
      ),
      [TOOLBAR_BUTTONS.Decrease]: () => (
        <Button
          title={TOOLBAR_TITLES[TOOLBAR_BUTTONS.Decrease]}
          iconButton="kbase kbase-editor-indent-decrease"
          variant={'text'}
          color={'grey'}
          onClick={() => decreaseIndent(editor)}
          disabled={disabled}
        />
      ),
      [TOOLBAR_BUTTONS.UploadImage]: () => {
        const disabled = isDisabled(TOOLBAR_BUTTONS.UploadImage)

        return <ImageButton disabled={disabled} />
      },
      [TOOLBAR_BUTTONS.UploadFile]: () => {
        const disabled = isDisabled(TOOLBAR_BUTTONS.UploadFile)

        return <FileButton disabled={disabled} />
      },
      [TOOLBAR_BUTTONS.Link]: () => {
        const disabled = isDisabled(TOOLBAR_BUTTONS.Link)

        return <LinkButton type="text" disabled={disabled} />
      },
      [TOOLBAR_BUTTONS.LinkButton]: () => {
        const disabled = isDisabled(TOOLBAR_BUTTONS.LinkButton)

        return <LinkButton disabled={disabled} />
      },
      [TOOLBAR_BUTTONS.Separator]: () => {
        const disabled = isDisabled(TOOLBAR_BUTTONS.Separator)

        return <SeparatorButton disabled={disabled} />
      },
      [TOOLBAR_BUTTONS.HorizontalRule]: () => {
        const disabled = isDisabled(TOOLBAR_BUTTONS.HorizontalRule)

        return (
          <Button
            title={TOOLBAR_TITLES[TOOLBAR_BUTTONS.HorizontalRule]}
            iconButton={'kbase kbase-minus'}
            variant={'text'}
            color={'grey'}
            // empty settings → a bare <hr/> (no inline height/width/background); insertSeparator
            // still adds the trailing empty line so the cursor is not trapped after the rule.
            onClick={() => insertSeparator(editor, {})}
            disabled={disabled}
          />
        )
      },
      [TOOLBAR_BUTTONS.IFrame]: () => {
        const disabled = isDisabled(TOOLBAR_BUTTONS.IFrame)

        return <IFrameButton disabled={disabled} />
      },
      [TOOLBAR_BUTTONS.Action]: () => {
        const disabled = isDisabled(TOOLBAR_BUTTONS.Action)

        return <ActionBoxButton disabled={disabled} />
      },
      [TOOLBAR_BUTTONS.Video]: () => {
        const disabled = isDisabled(TOOLBAR_BUTTONS.Video)

        return <VideoButton disabled={disabled} />
      },
      [TOOLBAR_BUTTONS.ColumnOptions]: () => {
        const disabled = isDisabled(TOOLBAR_BUTTONS.ColumnOptions)

        return <ColumnOptionsButton disabled={disabled} />
      },
      [TOOLBAR_BUTTONS.Table]: () => {
        const disabled = isDisabled(TOOLBAR_BUTTONS.Table)

        return <TableButton disabled={disabled} />
      },
    }
  }, [editor, isDisabled, disabled, fontSizes])

  return { toolbarButtons }
}
